import { LengthUnit } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import { 
    ContentBlockTargetFetchMode, 
    ContentDisplayAreaType, 
    CustomReaderViewContentLayout, 
    NavigationCollectionType, 
    ReaderDocumentEventState
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { ResponsiveViewRule } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine";
import { 
    FlipBookRenderer, 
    SingleDocumentScrollRenderer, 
    SinglePageSwipeRenderer, 
    SpreadSwipeRenderer, 
    StackRenderer 
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-renderer";
import { CirrusReaderReadingStats } from "../shared/CirrusReaderReadingStats";
import { CirrusReaderAppUiDefaults } from "../shared/CirrusReaderAppUiDefaults";
import equal from 'fast-deep-equal';
import type { IPaginatedRendererOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-renderer";
import type { IIntegerRange, ILength } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import type {
    CirrusActiveRendererChangedEventCallback,
    CirrusKeyboardEventCallback,
    CirrusMouseEngineEventCallback,
    CirrusNavigationIntentEventCallback,
    CirrusPageProgressionTimelineRecalculatedCallback, 
    CirrusPointerEngineEventCallback, 
    CirrusReaderViewEventCallback, 
    CirrusReaderViewReadingAreaChangedEventCallback, 
    CirrusReaderViewScrollEventCallback, 
    CirrusSelectionChangedEventCallback, 
    CirrusSwipeNavigationProgressCallback, 
    CirrusViewTransformChangedCallback, 
    ICirrusReaderContentPositionData, 
    ICirrusReaderNavigationItem, 
    ICirrusReaderPublicationStyleOptions, 
    ICirrusReaderReadingProgressionTimeline, 
    ICirrusReaderViewOptions, 
    ICirrusReaderVisibleRangeData} from "../shared/CirrusReaderModel";
import type { 
    ILocator, 
    ILocatorData
} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import type { 
    IActiveRendererChangedEngineEvent,
    IContentLocation, 
    IFetchNavigationItemReferencesResult, 
    IFocusOnReadingPositionOptions, 
    IKeyboardEngineEvent, 
    IMouseEngineEvent, 
    INavigationIntentEngineEvent, 
    IPageProgressionTimelineEngineEvent, 
    IPointerEngineEvent, 
    IReaderPublicationNavigationItemReference, 
    IReaderView, 
    IReaderViewEngineEvent, 
    IReaderViewGotoOptions, 
    IReaderViewOptions, 
    IReaderViewTransformEngineEvent, 
    IRenderer, 
    IRendererScrollEngineEvent, 
    ISelectionChangedEngineEvent, 
    ISwipeNavigationGestureProgressEngineEvent, 
    IUnresolvedContentLocation, 
    IVisibleContentRect, 
    IVisibleContentRectData
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { RendererType } from "../../../../app/configuration/types";
type CirrusNavigationTree = ReturnType<typeof useCirrusNavigationTree>

export default defineNuxtPlugin({
    name: 'cirrus-reader-view',
    dependsOn: [
        'cirrus-reader-reading-system',
        'cirrus-reader-publication',
        'i18n:plugin'
    ],
    enforce: 'pre',
    async setup (_nuxtApp) {
        const { variant } = useAppVariant();
        if (variant.value !== 'reader') return {};

        console.info('[CirrusPlugin] CirrusReaderView.setup()');

        const { $cirrusReaderPublication, $cirrusReaderReadingSystem, $i18n } = useNuxtApp();
        
        const cirrusReaderUiStore = useCirrusReaderUiStore();

        let _ignoreAspectRatio: boolean = false;
        let _useReducedMotion: boolean = false;
        let _scrollOverflowingTables: boolean = false;

        let _currentVisibleContentRects: IVisibleContentRect[] | undefined = undefined;

        let _navigationIntentCallback: CirrusNavigationIntentEventCallback | undefined;
        let _readingPositionUpdatedCallback: CirrusReaderViewEventCallback | undefined;
        let _selectionChangedCallback: CirrusSelectionChangedEventCallback | undefined;
        let _clickCallback: CirrusMouseEngineEventCallback | undefined;
        let _pointerDownCallback: CirrusPointerEngineEventCallback | undefined;
        let _pointerUpCallback: CirrusPointerEngineEventCallback | undefined;
        let _visibleRangeChangedCallback: CirrusReaderViewEventCallback | undefined;
        let _visibleContentRenderedCallback: CirrusReaderViewEventCallback | undefined;
        let _visiblePagesChangedCallback: CirrusReaderViewEventCallback | undefined;
        let _keyDownCallback: CirrusKeyboardEventCallback | undefined;
        let _keyUpCallback: CirrusKeyboardEventCallback | undefined;
        let _activeViewTransformChangedCallback: CirrusViewTransformChangedCallback | undefined;
        let _activeRendererChangedCallback: CirrusActiveRendererChangedEventCallback | undefined;
        let _swipeNavigationGestureProgressCallback: CirrusSwipeNavigationProgressCallback | undefined;
        let _rendererTransitionStartedCallback: CirrusReaderViewEventCallback | undefined;
        let _rendererTransitionEndedCallback: CirrusReaderViewEventCallback | undefined;
        let _rendererScrollStartedCallback: CirrusReaderViewScrollEventCallback | undefined;
        let _rendererScrollEndedCallback: CirrusReaderViewScrollEventCallback | undefined;
        let _rendererScrollCanceledCallback: CirrusReaderViewEventCallback | undefined;
        let _rendererScrollChangedCallback: CirrusReaderViewScrollEventCallback | undefined;
        let _readingAreaChangedCallback: CirrusReaderViewReadingAreaChangedEventCallback | undefined;
        let _pageProgressionTimelineRecalculatedCallback: CirrusPageProgressionTimelineRecalculatedCallback | undefined;
        
        let _cirrusReaderView: IReaderView;
        let _readerViewElement: HTMLElement;
        let _readingStats: CirrusReaderReadingStats | undefined;

        const initCirrusReaderView = async (): Promise<void> => {
            console.log("CirrusReaderView.initCirrusReaderView");

            _createRenderers();

            const accent = computed(() => {
                return useCirrusReaderSettingsStore().theme?.accent;
            });
            const { logoUrl } = await useClientLogo();
            _cirrusReaderView.setContentOnLoading({
                layout: CustomReaderViewContentLayout.FLEX_CENTER,
                renderTo: (containerElement: HTMLElement) => {
                    containerElement.innerHTML = logoUrl.value
                    ? `<div class="flex items-center justify-center h-screen">
                        <div class="relative w-16 h-16">
                            <div class="absolute inset-0 rounded-full border-4 animate-spin" style="border-color: ${accent.value}; border-top-color: transparent;"></div>
                            <div class="absolute inset-0 flex items-center justify-center">
                                <img 
                                    src="${logoUrl.value}"
                                    alt="Logo"
                                    class="w-8 max-h-8"
                                    tabindex="0"
                                />
                            </div>
                        </div>
                    </div>` 
                    : $i18n.t('messages.loading');
                },
            });
    
            _cirrusReaderView.setContentOnLoadError({
                layout: CustomReaderViewContentLayout.FLEX_CENTER,
                renderTo: (containerElement: HTMLElement) => {
                    containerElement.innerHTML = $i18n.t('messages.an_error_occured_whilst_rendering');
                },
            });

            _cirrusReaderView.setActiveRenderer(_cirrusReaderView.getRendererByName(useCirrusReaderSettingsStore().activeRenderer));
    
            try {
                _cirrusReaderView.renderTo(_readerViewElement);
            } catch (error) {
                console.warn("CirrusReaderView.initCirrusReaderView: An error occurred whilst attending to render.", error);
            }
    
            _readingStats = new CirrusReaderReadingStats();

            _createEventListeners();

            setCirrusReaderViewOptions({
                colibrioReaderViewOptions: {
                    publicationStyleOptions: {
                        palette: useCirrusReaderSettingsStore().theme
                    }
                }
            });

            if ($cirrusReaderPublication.isFixedLayout) {
                // If our loaded publication is a fixed layout, we want to remove settings which are specifically for reflowables
                useCirrusConfigStore().hideReflowableConfigSettings();
            }
        }

        const initReaderView = (
            readerViewElement: HTMLElement,
        ) => {
            console.log("CirrusReaderView.initReaderView",);
            _readerViewElement = readerViewElement;
            _cirrusReaderView = $cirrusReaderReadingSystem.createReaderView({});
            _cirrusReaderView.setReaderDocuments($cirrusReaderPublication.colibrioReaderPublication.getSpine());
        }


        
        /**
         * Navigational Utilites / Helpers
         */
        const canPerformPrevious = (): boolean => {
            return _cirrusReaderView.canPerformPrevious();
        }
    
        const canPerformNext = (): boolean => {
            return _cirrusReaderView.canPerformNext();
        }
    
        const canPerformGoTo = (): boolean => {
            return _cirrusReaderView.canPerformGoTo();
        }
    
        const previous = async (): Promise<void> => {
            return _cirrusReaderView.previous();
        }
    
        const next = async (): Promise<void> => {
            return _cirrusReaderView.next();
        }
    
        const goTo = async (
            location: ILocator | ILocatorData | IUnresolvedContentLocation | IContentLocation,
            options?: IReaderViewGotoOptions,
        ): Promise<void> => {
            return _cirrusReaderView.goTo(location, options);
        }
    
        const goToStart = async (): Promise<void> => {
            return _cirrusReaderView.goToStart();
        }
    
        const isAtStart = (): boolean  => {
            return _cirrusReaderView.isAtStart();
        }




        const refresh = (force: boolean = false): void => {
            _cirrusReaderView?.refresh(force);
        }

        const getReadingPosition = (): IContentLocation | null => {
            return _cirrusReaderView.getReadingPosition();
        }

        const getReadingPositionData = async (): Promise<ICirrusReaderContentPositionData | undefined> => {
            const readingPosition = getReadingPosition();
            if (!readingPosition) {
                return undefined;
            }
    
            return fetchContentPositionData(readingPosition);
        }

        const getVisibleRange = (): IContentLocation | null => {
            return _cirrusReaderView.getVisibleRange();
        }

        const focusOnReadingPosition = async (options: IFocusOnReadingPositionOptions): Promise<void> => {
            return _cirrusReaderView.focusOnReadingPosition(options).catch((e) => {
                console.warn('focusOnReadingPosition() failed', e);
            });
        }

        const fetchContentPositionData = async (contentLocation: IContentLocation): Promise<ICirrusReaderContentPositionData> => {
            const navigationItems: IFetchNavigationItemReferencesResult | undefined = await contentLocation.fetchNavigationItemReferences({
                greedy: true,
                collectionTypes: [
                    NavigationCollectionType.TOC,
                    NavigationCollectionType.LANDMARKS,
                    NavigationCollectionType.PAGE_LIST,
                ],
            });
    
            const navigationItemsData: ICirrusReaderNavigationItem[] | undefined = navigationItems.getItemsInRange()?.map((item: IReaderPublicationNavigationItemReference): ICirrusReaderNavigationItem => {
                return {
                    title: item.getNavigationItem().getTextContent(),
                    collectionType: item.getNavigationCollection().getType(),
                    children: [],
                    locator: item.getNavigationItem().getLocator()?.toString(),
                };
            });
    
            const contentBlockTargets = await contentLocation.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING_OR_NEAREST_AFTER);
            const contentPositionClientRects = (await fetchVisibleContentRectDataForContentLocation(contentLocation)) || [];
            const syntheticPageData = await useCirrusPageListStore().fetchPagesForLocator(contentLocation.getLocator())
            const readerDocuments = contentLocation.getReaderDocuments();
            return {
                documentIndex: (readerDocuments[0]) ? readerDocuments[0].getSourceContentDocument().getIndexInSpine() : undefined,
                locator: contentLocation.getLocator().toString(),
                contentBlockData: contentBlockTargets[0] ?
                    contentBlockTargets[0].getContentBlock().toSerializableData({
                        createLocators: true,
                        recursive: false,
                    }) :
                    undefined,
                navItemData: navigationItemsData,
                timestamp: Date.now(),
                contentRects: contentPositionClientRects,
                syntheticPageData
            };
        }

        const fetchVisibleRangeData = async (
            navigationTree?: CirrusNavigationTree,
            readingProgressionTimeline?: ICirrusReaderReadingProgressionTimeline,
        ): Promise<ICirrusReaderVisibleRangeData | undefined> => {
            const visibleRange = _cirrusReaderView?.getVisibleRange();
            const locator = visibleRange?.getLocator();
    
            if (!visibleRange || !locator) {
                return undefined;
            }
            let timelineRange: IIntegerRange | undefined;
            let readingProgression: number | undefined;
            let rangeTimelineStartPosition: number | undefined;
            let rangeTimelineEndPosition: number | undefined;
    
            if (readingProgressionTimeline) {
                rangeTimelineStartPosition = await readingProgressionTimeline.fetchTimelinePosition(visibleRange.collapseToStart().getLocator());
                rangeTimelineEndPosition = await readingProgressionTimeline.fetchTimelinePosition(visibleRange.collapseToEnd().getLocator());
                readingProgression = rangeTimelineStartPosition ?
                    rangeTimelineStartPosition / readingProgressionTimeline.getLength() :
                    undefined;
    
                if (rangeTimelineStartPosition && rangeTimelineEndPosition) {
                    timelineRange = {
                        start: rangeTimelineStartPosition,
                        end: rangeTimelineEndPosition,
                    };
                }
            }
    
            const navItems: ICirrusReaderNavigationItem[] = [];

            const navItemRefs = await navigationTree?.fetchItemsInVisibleRange([
                NavigationCollectionType.TOC,
                NavigationCollectionType.PAGE_LIST,
                NavigationCollectionType.LANDMARKS,
            ], true, getVisibleRange());
    
            navItemRefs?.forEach((ref: IReaderPublicationNavigationItemReference) => {
                navItems.push({
                    locator: ref.getNavigationItem()?.getLocator()?.toString(),
                    children: [],
                    collectionType: ref.getNavigationCollection().getType(),
                    title: ref.getNavigationItem().getTextContent(),
                });
    
            });
    
            if (visibleRange !== _cirrusReaderView?.getVisibleRange()) {
                return undefined;
            }
    
            const publicationIsAtStart = await visibleRangeIsAtStartOfPublication();
            const publicationIsAtEnd = await visibleRangeIsAtEndOfPublication();
            const documentIsAtStart = await visibleRangeIsAtStartOfDocument();
            const documentIsAtEnd = await visibleRangeIsAtEndOfDocument();

            let visibleContentRects: IVisibleContentRectData[] = [];
    
            for (const page of _cirrusReaderView.getVisiblePages()) {
                visibleContentRects = await fetchVisibleContentRectDataForContentLocation(page.getRangeContentLocation());
            }
    
            const contentResolverForVisibleContent = await visibleRange.fetchContentResolver();
            const visibleTextContent: string = contentResolverForVisibleContent.getTextContent();
    
            return {
                locator,
                timelinePosition: rangeTimelineStartPosition,
                timelineRange,
                readingProgression,
                bookmarks: [],
                highlights: [],
                navItems: navItems || [],
                documentIsAtStart,
                documentIsAtEnd,
                publicationIsAtStart,
                publicationIsAtEnd,
                visibleTextContent,
                visibleContentRects,
            };
        }

        const fetchVisibleContentRectDataForContentLocator = async (locator: string): Promise<IVisibleContentRectData[]> => {
            console.log("CirrusReaderView.fetchVisibleContentRectDataForContentLocator");
            if (_cirrusReaderView.getReaderPublications().length > 0){
                const contentLocation = await _cirrusReaderView.getReaderPublications()[0]!.fetchContentLocation(locator);
                const pages = _cirrusReaderView.getVisiblePages();
                let visiblePageIndex = 0;
                for (const page of pages){
                    if (page.getRangeContentLocation().intersects(contentLocation)) break;
                    visiblePageIndex++;
                }
                const visiblePage = pages[visiblePageIndex];
                if (visiblePage){
                    const locationClientRectsResult = await visiblePage.fetchRectsForVisibleContent(contentLocation, {
                        includeNodeData: true
                    });
                    return locationClientRectsResult.rects.map((rect)=>rect.toJSON());
                }
            }
            return [];
        }


        const fetchVisibleContentRectDataForContentLocation = async (contentLocation: IContentLocation): Promise<IVisibleContentRectData[]> => {
            if (!_currentVisibleContentRects) {
                try {
                    await updateAndFetchVisibleContentRects();
                } catch (err) {
                    console.log('CirrusReaderView.fetchVisibleContentRectDataForContentLocation: this._currentVisibleContentRects is undefined.', err);
                    return [];
                }
            } else {
    
                let matchingVisibleContentRects = _currentVisibleContentRects.filter((rect: IVisibleContentRect) => {
                    return rect.contentLocation.intersects(contentLocation);
                });
        
                if (matchingVisibleContentRects.length === 0) {
                    const nextContentRect = _currentVisibleContentRects.find((rect: IVisibleContentRect) => {
                        return rect.contentLocation.isAfter(contentLocation);
                    });
        
                    matchingVisibleContentRects = nextContentRect ? [nextContentRect] : [];
                }
        
                return matchingVisibleContentRects.map(rect => rect.toJSON());
            }
            return [];
        }


        const updateAndFetchVisibleContentRects = async(): Promise<IVisibleContentRect[]> => {

            const visibleRange = _cirrusReaderView.getVisibleRange();

            if (!visibleRange) {
                return [];
            }

            _currentVisibleContentRects = undefined;
    
            const contentPositionClientRects: IVisibleContentRect[] = [];
            const pages = _cirrusReaderView.getVisiblePages();
    
            for (const page of pages) {

                if (!_cirrusReaderView.getVisibleRange()?.contains(visibleRange)) {
                    continue;
                }

                const locationRectsResult = await page.fetchRectsForVisibleContent(visibleRange, {
                    includeNodeData: true,
                });

                if (locationRectsResult.rects.length > 0) {
                    contentPositionClientRects.push(...locationRectsResult.rects);
                }
            }
    
            _currentVisibleContentRects = contentPositionClientRects;

            return contentPositionClientRects;
        }

        const fetchContentPositionDataForVisibleContentLocation = async (contentLocation: IContentLocation): Promise<ICirrusReaderContentPositionData> => {
            const contentPositionData = await fetchContentPositionData(contentLocation);
            let contentPositionClientRects: IVisibleContentRectData[] | undefined = undefined;
            if(!useCirrusReaderUtils().isPdfReaderPublication($cirrusReaderPublication.colibrioReaderPublication) && _cirrusReaderView.getVisibleRange()?.contains(contentLocation)) {
                contentPositionClientRects = (await fetchVisibleContentRectDataForContentLocation(contentLocation)) || [];
            }

            if(contentPositionClientRects) {
                contentPositionData.contentRects = contentPositionClientRects;
            }

            return contentPositionData;
        }


        /*
        *
        * This utility method checks if the content that is currently visible on the screen is the first
        * content available in the publication.
        *
        * */
        const visibleRangeIsAtStartOfPublication = async(): Promise<boolean> => {
            const visibleRange = _cirrusReaderView?.getVisibleRange();
            const colibrioReaderPublication = $cirrusReaderReadingSystem.colibrioReaderPublication;
            const locator = visibleRange?.getLocator();

            if (colibrioReaderPublication && visibleRange && locator) {
                const visibleRangeStart = visibleRange.collapseToStart();
                const firstReaderDocumentInPublication = colibrioReaderPublication?.getSpine()[0];
                const firstReaderDocumentContentLocation = await firstReaderDocumentInPublication?.fetchContentLocationForEntireDocument();
                const firstReaderDocumentStart = firstReaderDocumentContentLocation?.collapseToStart();
                return firstReaderDocumentStart?.equals(visibleRangeStart) || false;
            } else {
                console.warn('CirrusReaderView.readingPositionIsAtStartOfPublication: Could not resolve a locator or visibleRange.');
                return false;
            }
        }

        /*
        *
        * This utility method checks if the content that is currently visible on the screen is the last
        * content available in the publication.
        *
        * */
        const visibleRangeIsAtEndOfPublication = async(): Promise<boolean> => {
            const visibleRange = _cirrusReaderView?.getVisibleRange();
            const colibrioReaderPublication = $cirrusReaderReadingSystem.colibrioReaderPublication;
            const locator = visibleRange?.getLocator();

            if (colibrioReaderPublication && visibleRange && locator) {
                const visibleRangeEnd = visibleRange.collapseToEnd();
                const readerPublicationLength = colibrioReaderPublication.getSpine().length || 0;
                const lastReaderDocumentInPublication = colibrioReaderPublication.getSpine()[readerPublicationLength - 1];
                const lastReaderDocumentContentLocation = await lastReaderDocumentInPublication?.fetchContentLocationForEntireDocument();
                const lastReaderDocumentEnd = lastReaderDocumentContentLocation?.collapseToEnd();
                return lastReaderDocumentEnd?.equals(visibleRangeEnd) || false;
            } else {
                console.warn('CirrusReaderView.readingPositionIsAtEndOfPublication: Could not resolve a locator or visibleRange.');
                return false;
            }
        }

        /*
        *
        * This utility method checks if the content that is currently visible is at the very start of the content document.
        * In other words that the start `IContentLocation` of the visible range is equal to the start IContentLocation` of
        * the entire document.
        *
        * */
        const visibleRangeIsAtStartOfDocument = async(): Promise<boolean> => {
            const visibleRange = _cirrusReaderView?.getVisibleRange();
            const locator = visibleRange?.getLocator();

            if (visibleRange && locator) {
                const visibleRangeStart = visibleRange.collapseToStart();
                const visibleRangeEnd = visibleRange.collapseToEnd();
                const visibleReaderDocument = visibleRangeEnd.getReaderDocuments()[0];
                if (visibleReaderDocument){
                    const visibleReaderDocumentContentLocation = await visibleReaderDocument.fetchContentLocationForEntireDocument();
                    const visibleReaderDocumentStart = visibleReaderDocumentContentLocation.collapseToStart();
                    return visibleReaderDocumentStart.equals(visibleRangeStart);
                }
            }
            console.warn('CirrusReaderView.readingPositionIsAtStartOfDocument: Could not resolve a locator or visibleRange.');
            return false;
        }

        /*
        *
        * This utility method checks if the content that is currently visible is at the very end of the content document.
        * In other words that the end `IContentLocation` of the visible range is equal to the end IContentLocation` of
        * the entire document.
        *
        * */
        const visibleRangeIsAtEndOfDocument = async(): Promise<boolean> => {
            const visibleRange = _cirrusReaderView?.getVisibleRange();
            const locator = visibleRange?.getLocator();

            if (visibleRange && locator) {
                const visibleRangeEnd = visibleRange.collapseToEnd();
                const visibleReaderDocument = visibleRangeEnd.getReaderDocuments()[0];
                if (visibleReaderDocument){
                    const visibleReaderDocumentContentLocation = await visibleReaderDocument.fetchContentLocationForEntireDocument();
                    const visibleReaderDocumentEnd = visibleReaderDocumentContentLocation.collapseToEnd();
                    return visibleReaderDocumentEnd.equals(visibleRangeEnd);
                }
            }
            console.warn('CirrusReaderView.readingPositionIsAtEndOfDocument: Could not resolve a locator or visibleRange.');
            return false;
        }

        const getCirrusReaderViewOptions = (): ICirrusReaderViewOptions => {
            return _getCurrentReaderViewOptionsState();
        }

        const setCirrusReaderViewOptions = (viewOptions: ICirrusReaderViewOptions): void => {
            console.log("CirrusReaderView.setCirrusReaderViewOptions");

            const mergedReaderViewOptions: ICirrusReaderViewOptions = useObjectUtils().deepMerge(getCirrusReaderViewOptions(), viewOptions);
            if(mergedReaderViewOptions.colibrioRendererOptions !== undefined && !equal(_cirrusReaderView.getActiveRenderer()?.getOptions(), mergedReaderViewOptions.colibrioRendererOptions)) {
                _cirrusReaderView.getActiveRenderer()?.setOptions(mergedReaderViewOptions.colibrioRendererOptions);
            }
            
            if (mergedReaderViewOptions.useReducedMotion !== undefined && mergedReaderViewOptions.useReducedMotion !== getCirrusReaderViewOptions().useReducedMotion) {
                setUseReductedMotion(mergedReaderViewOptions.useReducedMotion);
            }

            if (mergedReaderViewOptions.scrollOverflowingTables !== undefined && mergedReaderViewOptions.scrollOverflowingTables !== getCirrusReaderViewOptions().scrollOverflowingTables) {
                setScrollOverflowingTables(mergedReaderViewOptions.scrollOverflowingTables);
            }

            if (mergedReaderViewOptions.ignoreAspectRatio !== undefined && mergedReaderViewOptions.ignoreAspectRatio !== getCirrusReaderViewOptions().ignoreAspectRatio){
                setIgnoreAspectRatio(mergedReaderViewOptions.ignoreAspectRatio);
            }

            if (mergedReaderViewOptions.activeRendererTypeName !== undefined && _getActiveRendererName() !== mergedReaderViewOptions.activeRendererTypeName) {
                _changeRenderer(mergedReaderViewOptions.activeRendererTypeName, mergedReaderViewOptions.ignoreAspectRatio);
            }
            
            const colibrioReaderViewOptions = _cirrusReaderView.getOptions();
            const colibrioReaderViewOptionsClone = JSON.parse(JSON.stringify(colibrioReaderViewOptions)) as IReaderViewOptions;
            const viewOptionsHasChanges = !equal(colibrioReaderViewOptionsClone, viewOptions.colibrioReaderViewOptions)
            if (mergedReaderViewOptions.colibrioReaderViewOptions !== undefined && Object.entries(mergedReaderViewOptions.colibrioReaderViewOptions).length > 0 && viewOptionsHasChanges) {
                _cirrusReaderView.setOptions(mergedReaderViewOptions.colibrioReaderViewOptions);
            }
        }


        /**
         * Private Functions
         */
        const _createRenderers = (): void => {
            console.log("CirrusReaderView._createRenderers");

            const rendererBackgroundColor: string | undefined = useCirrusReaderSettingsStore().availableThemes.filter(
                (theme) => theme.name.toLowerCase() == useCirrusReaderSettingsStore().theme?.toString().toLowerCase()
            )[0]?.backgroundLight;

            if (useCirrusReaderSettingsStore().availableRenderers.includes(RendererType.enum.FlipBookRenderer)){
                const colibrioFlipBookRenderer = new FlipBookRenderer({
                    name: 'FlipBookRenderer',
                    disableAnimations: false,
                    pageBackgroundColor: rendererBackgroundColor,
                });
                _cirrusReaderView.addRenderer(colibrioFlipBookRenderer, new ResponsiveViewRule('(orientation: landscape) and (min-width: 600px)'));
            }

            if (useCirrusReaderSettingsStore().availableRenderers.includes(RendererType.enum.StackRenderer)){
                const colibrioStackRenderer = new StackRenderer({
                    name: 'StackRenderer',
                    pageBackgroundColor: rendererBackgroundColor,
                });
        
                const colibrioStackRendererMobile = new StackRenderer({
                    name: 'StackRenderer',
                    showRendererBackgroundShadow: false,
                    disableAnimations: false,
                    pageBackgroundColor: rendererBackgroundColor,
                });

                _cirrusReaderView.addRenderer(colibrioStackRenderer, new ResponsiveViewRule('(min-width: 450px)'));
                _cirrusReaderView.addRenderer(colibrioStackRendererMobile, new ResponsiveViewRule('(max-width: 450px)'));
            }

            if (useCirrusReaderSettingsStore().availableRenderers.includes(RendererType.enum.SingleDocumentScrollRenderer)){
                const colibrioScrollRenderer = new SingleDocumentScrollRenderer({
                    name: 'SingleDocumentScrollRenderer',
                    readingAreaOffsetTop: {
                        value: 5,
                        unit: LengthUnit.PERCENT,
                    },
                    pageBackgroundColor: rendererBackgroundColor,
                });
                _cirrusReaderView.addRenderer(colibrioScrollRenderer);    
            }

            const colibrioSwipeRendererSingle = new SinglePageSwipeRenderer({
                name: 'SinglePageSwipeRenderer',
                disableAnimations: false,
                pageBackgroundColor: rendererBackgroundColor,
            });
            
            const colibrioSwipeRendererSpread = new SpreadSwipeRenderer({
                name: 'SpreadSwipeRenderer',
                disableAnimations: false,
                pageBackgroundColor: rendererBackgroundColor,
            });

            _cirrusReaderView.addRenderer(colibrioSwipeRendererSingle);
            _cirrusReaderView.addRenderer(colibrioSwipeRendererSpread, new ResponsiveViewRule('(orientation: landscape) and (min-width: 600px)'));
        }

        const _changeRenderer = (rendererName: string, ignoreAspectRatio: boolean | undefined): void => {
            console.log("CirrusReaderView._changeRenderer");

            if (_cirrusReaderView) {
                if (rendererName !== RendererType.enum.__responsive__) {
                    if (Object.values(RendererType).includes(rendererName as RendererType)) {
                        const renderer = _cirrusReaderView.getRendererByName(rendererName);
                        if (renderer) {
                            if (rendererName != RendererType.enum.SingleDocumentScrollRenderer) {
                                const paginatedRendererOptions: IPaginatedRendererOptions = {
                                    disableAnimations: false,
                                };
                                renderer.setOptions(paginatedRendererOptions);
                            }
                            _cirrusReaderView.setResponsiveRendererSelectionEnabled(false);
                            _cirrusReaderView.setActiveRenderer(renderer);
                            if (ignoreAspectRatio !== undefined) setIgnoreAspectRatio(ignoreAspectRatio);
                        } else {
                            console.error('CirrusReaderView._changeRenderer: Renderer name "' + rendererName + '" does not exist');
                        }
                    } else {
                        console.error('CirrusReaderView._changeRenderer: Renderer name "' + rendererName + '" does not exist');
                    }
                } else {
                    _cirrusReaderView.setResponsiveRendererSelectionEnabled(true);
                    if (ignoreAspectRatio !== undefined) setIgnoreAspectRatio(ignoreAspectRatio);
                }
            } else {
                console.warn('CirrusReaderView._changeRenderer: There is no _cirrusReaderView instance, is the current publication an audiobook perhaps? ');
            }
        }
        
        const _getActiveRenderer = (): IRenderer | null => {
            return _cirrusReaderView.getActiveRenderer();
        }

        const _getActiveRendererName = (): RendererType | undefined => {
            const activeRendererName = _isResponsiveRendererSelectionEnabled() ? RendererType.enum.__responsive__ : _getActiveRenderer()?.getName();
            return activeRendererName as RendererType;
        }

        const _isResponsiveRendererSelectionEnabled = (): boolean => {
            return _cirrusReaderView.isResponsiveRendererSelectionEnabled();
        }

         const _isPaginatedRenderer = (activeRenderer: unknown): activeRenderer is StackRenderer | FlipBookRenderer | SinglePageSwipeRenderer | SpreadSwipeRenderer => {
            console.log("CirrusReaderView._isPaginatedRenderer");

            return activeRenderer instanceof StackRenderer ||
                activeRenderer instanceof FlipBookRenderer ||
                activeRenderer instanceof SinglePageSwipeRenderer ||
                activeRenderer instanceof SpreadSwipeRenderer;
        }

        const _getCurrentReaderViewOptionsState = (): ICirrusReaderViewOptions  => {
            const publicationStyleOptions = _cirrusReaderView.getOptions().publicationStyleOptions;
            return {
                colibrioReaderViewOptions: {
                    publicationStyleOptions,
                },
                ignoreAspectRatio: _ignoreAspectRatio,
                useReducedMotion: _useReducedMotion,
                scrollOverflowingTables: _scrollOverflowingTables,
                activeRendererTypeName: _isResponsiveRendererSelectionEnabled() ? RendererType.enum.__responsive__ : _getActiveRendererName(),
                colibrioRendererOptions: _cirrusReaderView.getActiveRenderer()?.getOptions(),
            };
        }        

        const _readingSystem_event_onClick = (ev: IMouseEngineEvent) => {
            if (_clickCallback) {
                _clickCallback(ev);
            }

            const srcEventData = ev.toJSON();
            if (useCirrusReaderUtils().eventDataIsMouseEngineEventData(ev.toJSON())){
                if (srcEventData.defaultPrevented || srcEventData.readerDocumentEventState === ReaderDocumentEventState.DEFAULT_PREVENTED) {
                    return;
                }
                const nodeData = srcEventData.target?.nodeData;
                const isMediaElement = nodeData ? (nodeData?.nodeName === 'img' ||
                                    nodeData?.nodeName === 'video' ||
                                    nodeData?.nodeName === 'audio') : false;
                
                if ($cirrusReaderReadingSystem.isInAccessibilityMode && !cirrusReaderUiStore.getActiveSelection() && !isMediaElement) {
                    const targetSelector = srcEventData.target?.locator?.selectors[0];
                    if (targetSelector) {
                        goTo('#' + targetSelector)
                        ev.preventDefault();
                        ev.stopImmediatePropagation();
                    }
                }
            }
        }

        const _readingSystem_event_onNavigationIntent = (ev: INavigationIntentEngineEvent) => {
            if (_navigationIntentCallback){
                _navigationIntentCallback(ev);
            }
        }

        const _readingSystem_event_onReadingPositionChanged = (ev: IReaderViewEngineEvent) => {
            if (_readingPositionUpdatedCallback){
                _readingPositionUpdatedCallback(ev);
            }
        }

        const _readingSystem_event_onPointerDown = (ev: IPointerEngineEvent) => {
            if (_pointerDownCallback){
                _pointerDownCallback(ev);
            }
        }

        const _readingSystem_event_onPointerUp = (ev: IPointerEngineEvent) => {
            if (_pointerUpCallback){
                _pointerUpCallback(ev)
            }
        }

        const _readingSystem_event_onVisibleRangeChanged = (ev: IReaderViewEngineEvent) =>{
            if (_visibleRangeChangedCallback){
                _visibleRangeChangedCallback(ev);
            }
        }
         
        const _readingSystem_event_onVisiblePagesChanged = (ev: IReaderViewEngineEvent) => {
            if (_visiblePagesChangedCallback){
                _visiblePagesChangedCallback(ev);
            }
        }

        const _readingSystem_event_onVisibleContentRendered = (ev: IReaderViewEngineEvent) => {
            if (_visibleContentRenderedCallback){
                _visibleContentRenderedCallback(ev);
            }
        }

        const _readingSystem_event_onActiveTransformChanged = (ev: IReaderViewTransformEngineEvent) => {
            if (_activeViewTransformChangedCallback){
                _activeViewTransformChangedCallback(ev.transform);
            }
        }

        const _readingSystem_event_onSelectionChanged = async (ev: ISelectionChangedEngineEvent, selectionRectData?: IVisibleContentRectData[] | undefined) => {
            if (_selectionChangedCallback) {
                _selectionChangedCallback(ev, selectionRectData);
            }
        }

        const _readingSystem_event_onKeyDown = (ev: IKeyboardEngineEvent) => {
            if (_keyDownCallback) {
                _keyDownCallback(ev);
            }
        }

        const _readingSystem_event_onKeyUp = (ev: IKeyboardEngineEvent) => {
            if (_keyUpCallback) {
                _keyUpCallback(ev);
            }
        }

        const _readingSystem_event_onRendererTransitionStarted = (ev: IReaderViewEngineEvent) => {
            if (_rendererTransitionStartedCallback){
                _rendererTransitionStartedCallback(ev);
            }
        }

        const _readingSystem_event_onRendererTransitionEnded = (ev: IReaderViewEngineEvent) => {
            if (_rendererTransitionEndedCallback){
                _rendererTransitionEndedCallback(ev);
            }
        }

        const _readingSystem_event_onRendererScrollStarted = (ev: IRendererScrollEngineEvent) => {
            if (_rendererScrollStartedCallback){
                _rendererScrollStartedCallback(ev);
            }
        }

        const _readingSystem_event_onRendererScrollEnded = (ev: IRendererScrollEngineEvent) => {
            if (_rendererScrollEndedCallback){
                _rendererScrollEndedCallback(ev)
            }
        }

        const _readingSystem_event_onRendererScrollCanceled = (ev: IReaderViewEngineEvent) => {
            if (_rendererScrollCanceledCallback){
                _rendererScrollCanceledCallback(ev)
            }
        }

        const _readingSystem_event_onRendererScrollChanged = (ev: IRendererScrollEngineEvent) => {
            if (_rendererScrollChangedCallback){
                _rendererScrollChangedCallback(ev)
            }
        }

        const _readingSystem_event_onSwipeNavigationGestureProgress = (ev: ISwipeNavigationGestureProgressEngineEvent) => {
            if (_swipeNavigationGestureProgressCallback){
                _swipeNavigationGestureProgressCallback(ev.toJSON());
            }
        }

        const _readingSystem_event_onReadingAreaRangeChanged = (ev: IReaderViewEngineEvent) => {
            if (_readingAreaChangedCallback){
                _readingAreaChangedCallback(ev)
            }
        }

        const _readingSystem_event_onActiveRendererChanged = (ev: IActiveRendererChangedEngineEvent) => {
            if (_activeRendererChangedCallback){
                _activeRendererChangedCallback(ev);
            }
        }

        const _readingSystem_event_onPageProgressionTimelineRecalculated = (ev: IPageProgressionTimelineEngineEvent) => {
            if (_pageProgressionTimelineRecalculatedCallback) {
                _pageProgressionTimelineRecalculatedCallback(ev);
            }
        }

        const _createEventListeners = () => {
            /** Create Reading System Event Handlers */

            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('click', _readingSystem_event_onClick);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('keydown', _readingSystem_event_onKeyDown);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('keyup', _readingSystem_event_onKeyUp);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('navigationIntent', _readingSystem_event_onNavigationIntent);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('readingPositionChanged', _readingSystem_event_onReadingPositionChanged);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('pointerdown', _readingSystem_event_onPointerDown);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('pointerup', _readingSystem_event_onPointerUp);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('selectionChanged', _readingSystem_event_onSelectionChanged);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('visibleRangeChanged', _readingSystem_event_onVisibleRangeChanged);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('visiblePagesChanged', _readingSystem_event_onVisiblePagesChanged);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('visibleContentRendered', _readingSystem_event_onVisibleContentRendered);
            $cirrusReaderReadingSystem.colibrioReadingSystem.addEngineEventListener('activeTransformChanged', _readingSystem_event_onActiveTransformChanged);

            _cirrusReaderView.addEngineEventListener('rendererTransitionStarted', _readingSystem_event_onRendererTransitionStarted);
            _cirrusReaderView.addEngineEventListener('rendererTransitionEnded', _readingSystem_event_onRendererTransitionEnded);
            _cirrusReaderView.addEngineEventListener('rendererScrollStarted', _readingSystem_event_onRendererScrollStarted);
            _cirrusReaderView.addEngineEventListener('rendererScrollEnded', _readingSystem_event_onRendererScrollEnded);
            _cirrusReaderView.addEngineEventListener('rendererScrollCanceled', _readingSystem_event_onRendererScrollCanceled);
            _cirrusReaderView.addEngineEventListener('rendererScrollChanged', _readingSystem_event_onRendererScrollChanged);
            _cirrusReaderView.addEngineEventListener('swipeNavigationGestureProgress', _readingSystem_event_onSwipeNavigationGestureProgress);
            _cirrusReaderView.addEngineEventListener('readingAreaRangeChanged', _readingSystem_event_onReadingAreaRangeChanged);
            _cirrusReaderView.addEngineEventListener('activeRendererChanged', _readingSystem_event_onActiveRendererChanged);
            _cirrusReaderView.addEngineEventListener('pageProgressionTimelineRecalculated', _readingSystem_event_onPageProgressionTimelineRecalculated);
                        
            /**
             * Subscribe to changes to the CirrusReaderStore
             * React to any change to settings
             */

            watch(
                useCirrusReaderSettingsStore(),
                (state) => {
                    const defaultOptions = CirrusReaderAppUiDefaults().options;
                    const viewOptions: ICirrusReaderViewOptions = {};
                    const publicationStyleOptions: ICirrusReaderPublicationStyleOptions | undefined = defaultOptions.publicationStyleOptions;
                    if (publicationStyleOptions) {
                        if (state.theme) {
                            publicationStyleOptions.palette = state.theme;
                        }
                        if (state.font && state.font !== 'components.publication_default') {
                            publicationStyleOptions.fontSet = state.availableFonts.filter((font) => font.name == state.font)[0];
                        }
                        if (state.fontSize) {
                            publicationStyleOptions.fontSizeScaleFactor = parseFloat(state.fontSize.replace('%', '')) / 100;
                        }
                        if (state.lineHeight) {
                            publicationStyleOptions.lineHeightScaleFactor = parseFloat(state.lineHeight.replace('%', '')) / 100;
                        }
                        if (state.letterSpacing && state.letterSpacing !== 'components.publication_default') {
                            publicationStyleOptions.letterSpacing = {unit: 'PERCENT', value: parseFloat(state.letterSpacing.replace('%', ''))} as ILength;
                        }
                        if (state.wordSpacing && state.wordSpacing !== 'components.publication_default') {
                            publicationStyleOptions.wordSpacing = {unit: 'PERCENT', value: parseFloat(state.wordSpacing.replace('%', ''))} as ILength;
                        }
                        if (state.ignoreAspectRatio !== null) {
                            viewOptions.ignoreAspectRatio = state.ignoreAspectRatio;
                        }
                        if (state.activeRenderer) {
                            _cirrusReaderView.setActiveRenderer(_cirrusReaderView.getRendererByName(state.activeRenderer));
                        }
                        viewOptions.colibrioReaderViewOptions = $cirrusReaderPublication.setPublicationStyleOptions(publicationStyleOptions);
                        setCirrusReaderViewOptions(viewOptions);
                    }
                }
            )
            
        }

        const setIgnoreAspectRatio = (ignoreAspectRatio: boolean) => {
            if (ignoreAspectRatio === _ignoreAspectRatio) {
                return;
            }

            _ignoreAspectRatio = ignoreAspectRatio;
            if (_ignoreAspectRatio) {
                _cirrusReaderView.setOptions({
                    contentDisplayAreaOptions: {
                        type: ContentDisplayAreaType.FILL,
                    }
                });
            } else {
                _cirrusReaderView.setOptions({
                    contentDisplayAreaOptions: {
                        type: ContentDisplayAreaType.CALCULATED_FROM_PUBLICATION_CONTENT,
                    }
                });
            }

            refresh(true);
        }

        const setUseReductedMotion = (useReducedMotion: boolean) => {
            if(_useReducedMotion === useReducedMotion) {
                return;
            }

            _useReducedMotion = useReducedMotion;
            if (_cirrusReaderView) {
                const activeRenderer = _cirrusReaderView.getActiveRenderer() as unknown;
                if (_isPaginatedRenderer(activeRenderer)) {
                    activeRenderer.setOptions({
                        disableAnimations: useReducedMotion,
                    });
                }
                if($cirrusReaderPublication.colibrioReaderPublication) {
                    const styleOptions = $cirrusReaderPublication.publicationStyleOptions;
                    if (styleOptions) {
                        styleOptions.disableCssAnimations = useReducedMotion;
                        $cirrusReaderPublication.setPublicationStyleOptions(styleOptions);
                    }

                    if (useCirrusReaderUtils().isEpubReaderPublication($cirrusReaderPublication.colibrioReaderPublication)) {
                        const readerPublicationOptions = $cirrusReaderPublication.colibrioReaderPublication.getOptions();
                        readerPublicationOptions.allowVideoElementAutoplay = !useReducedMotion;
                        $cirrusReaderPublication.colibrioReaderPublication.setOptions(readerPublicationOptions);
                    }

                    setCirrusReaderViewOptions({colibrioReaderViewOptions: {
                        transformManagerOptions: {
                            removeTransformAnimationDurationMs: useReducedMotion ? 0 : 300
                        }
                    }});
                }
            }

            refresh(true);
        }

        const setScrollOverflowingTables = (scrollOverflowingTables: boolean) => {
            if(_scrollOverflowingTables === scrollOverflowingTables) {
                return;
            }
    
            _scrollOverflowingTables = scrollOverflowingTables;
            const readerPublication = _cirrusReaderView.getReaderPublications()[0];
            const cirrusReaderUtils = useCirrusReaderUtils();

            if (readerPublication && cirrusReaderUtils.isEpubReaderPublication(readerPublication)) {
                const activeOptions = readerPublication.getOptions();
                const blacklistedSelectors = activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors;
    
                if(scrollOverflowingTables === true && !blacklistedSelectors.includes('table')) {
                    blacklistedSelectors.push('table');
                    activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors = blacklistedSelectors;
                } else {
                    const updatedBlacklist = blacklistedSelectors.filter((selector)=>{
                        return selector !== 'table'
                    });
                    activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors = updatedBlacklist;
                }

                $cirrusReaderPublication.publicationOptions = activeOptions;
                refresh(true);
            }
        }

        return {
            provide: {
                cirrusReaderView: {
                    initReaderView,
                    initCirrusReaderView,
                    getReadingPosition,
                    updateAndFetchVisibleContentRects,
                    fetchVisibleContentRectDataForContentLocator, 
                    fetchContentPositionDataForVisibleContentLocation,
                    fetchVisibleRangeData,
                    getReadingPositionData,
                    getVisibleRange,
                    focusOnReadingPosition,
                    fetchContentPositionData,
                    refresh,
                    canPerformPrevious,
                    canPerformNext,
                    canPerformGoTo,
                    previous,
                    next,
                    goTo,
                    goToStart,
                    isAtStart,
                    setCirrusReaderViewOptions,
                    get colibrioReaderView(): IReaderView{
                        return _cirrusReaderView;
                    },
                    get readingStats() {
                        return _readingStats;
                    },
                    set onReadingPositionChanged(callback: CirrusReaderViewEventCallback) {
                        _readingPositionUpdatedCallback = callback;
                    },
                    set onNavigationIntent(callback: CirrusNavigationIntentEventCallback) {
                        _navigationIntentCallback = callback;
                    },
                    set onSelectionChanged(callback: CirrusSelectionChangedEventCallback) {
                        _selectionChangedCallback = callback;
                    },
                    set onClick(callback: CirrusMouseEngineEventCallback) {
                        _clickCallback = callback;
                    },
                    set onPointerDown(callback: CirrusPointerEngineEventCallback) {
                        _pointerDownCallback = callback;
                    },
                    set onPointerUp(callback: CirrusPointerEngineEventCallback) {
                        _pointerUpCallback = callback;
                    },
                    set onVisibleRangeChanged(callback: CirrusReaderViewEventCallback) {
                        _visibleRangeChangedCallback = callback;
                    },
                    set onVisibleContentRendered(callback: CirrusReaderViewEventCallback) {
                        _visibleContentRenderedCallback = callback;
                    },
                    set onVisiblePagesChanged(callback: CirrusReaderViewEventCallback) {
                        _visiblePagesChangedCallback = callback;
                    },
                    set onKeyDown(callback: CirrusKeyboardEventCallback) {
                        _keyDownCallback = callback;
                    },
                    set onKeyUp(callback: CirrusKeyboardEventCallback) {
                        _keyUpCallback = callback;
                    },
                    set onActiveReaderViewTransformChanged(callback: CirrusViewTransformChangedCallback) {
                        _activeViewTransformChangedCallback = callback;
                    },
                    set onRendererTransitionStarted(callback: CirrusReaderViewEventCallback){
                        _rendererTransitionStartedCallback = callback;
                    },
                    set onRendererTransitionEnded(callback: CirrusReaderViewEventCallback){
                        _rendererTransitionEndedCallback = callback;
                    },
                    set onRendererScrollStarted(callback: CirrusReaderViewScrollEventCallback){
                        _rendererScrollStartedCallback = callback;
                    },
                    set onRendererScrollEnded(callback: CirrusReaderViewScrollEventCallback){
                        _rendererScrollEndedCallback = callback;
                    },
                    set onRendererScrollCanceled(callback: CirrusReaderViewEventCallback){
                        _rendererScrollCanceledCallback = callback;
                    },
                    set onRendererScrollChanged(callback: CirrusReaderViewScrollEventCallback){
                        _rendererScrollChangedCallback = callback;
                    },
                    set onReadingAreaChanged(callback: CirrusReaderViewReadingAreaChangedEventCallback){
                        _readingAreaChangedCallback = callback;
                    },
                    set onPageProgressionTimelineRecalculated(callback: CirrusPageProgressionTimelineRecalculatedCallback){
                        _pageProgressionTimelineRecalculatedCallback = callback;
                    },
                    get ignoreAspectRatio(): boolean {
                        return _ignoreAspectRatio;
                    },
                    set ignoreAspectRatio(ignoreAspectRatio: boolean) {
                        setIgnoreAspectRatio(ignoreAspectRatio);
                    },
                    get useReducedMotion(){
                        return _useReducedMotion;
                    },
                    set useReducedMotion(useReducedMotion: boolean) {
                        setUseReductedMotion(useReducedMotion);
                    },
                    get scrollOverflowingTables(){
                        return _scrollOverflowingTables;
                    },
                    set scrollOverflowingTables(scrollOverflowingTables: boolean) {
                        setScrollOverflowingTables(scrollOverflowingTables);
                    }
                }
            }
        }
    }
});
